import axios from 'axios';

// @ts-ignore
const API_URL = 'https://review-api-430889471.hobbiton.eviltech.fr/api/v1';

export const api = axios.create({
	baseURL: API_URL,
	headers: {
		'Content-Type': 'application/json',
		'Accept': 'application/json'
	}
});

export interface ApiResponse<T> {
	status: string;
	data: T;
	message: string;
	metadata: any;
}


/* ----------------------------
   Stockage local du token + Refresh automatique
----------------------------- */
let refreshTimer: NodeJS.Timeout | null = null;

export function setAuthToken(token: string | null, expiresIn?: number) {
	// Annuler le timer précédent
	if (refreshTimer) {
		clearTimeout(refreshTimer);
		refreshTimer = null;
	}

	if (!token) {
		localStorage.removeItem('token');
		localStorage.removeItem('token_expires_at');
		delete api.defaults.headers.common['Authorization'];
		return;
	}

	localStorage.setItem('token', token);
	api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

	// Stocker l'heure d'expiration si fournie
	if (expiresIn) {
		const expiresAt = Date.now() + (expiresIn * 1000);
		localStorage.setItem('token_expires_at', expiresAt.toString());

		// Programmer un refresh à 80% de la durée de vie du token
		const refreshIn = expiresIn * 0.8 * 1000; // 80% du TTL en ms

		refreshTimer = setTimeout(async () => {
			try {
				const currentToken = localStorage.getItem('token');
				if (!currentToken) return;

				const response = await axios.post(API_URL + '/jwt/refresh', {}, {
					headers: {
						'Authorization': `Bearer ${currentToken}`
					}
				});

				const newToken = response.data.access_token;
				const newExpiresIn = response.data.expires_in;
				setAuthToken(newToken, newExpiresIn);
			} catch (error) {
				console.error('❌ Échec du refresh automatique', error);
				setAuthToken(null);
			}
		}, refreshIn);
	}
}

// Charger le token au démarrage de l'app et gérer le refresh
const existingToken = localStorage.getItem('token');
if (existingToken) {
	api.defaults.headers.common['Authorization'] = `Bearer ${existingToken}`;

	// Vérifier si on doit programmer un refresh
	const expiresAt = localStorage.getItem('token_expires_at');
	if (expiresAt) {
		const now = Date.now();
		const expiry = parseInt(expiresAt);
		const remainingTime = Math.floor((expiry - now) / 1000); // en secondes

		if (remainingTime > 30) {
			// Si plus de 30 secondes restantes, reprogrammer le refresh normalement
			setAuthToken(existingToken, remainingTime);
		} else if (remainingTime > 0) {
			// Si moins de 30 secondes mais toujours valide, refresh immédiat
			axios.post(API_URL + '/jwt/refresh', {}, {
				headers: {
					'Authorization': `Bearer ${existingToken}`
				}
			})
			.then(response => {
				const newToken = response.data.access_token;
				const newExpiresIn = response.data.expires_in;
				setAuthToken(newToken, newExpiresIn);
			})
			.catch(error => {
				console.error('❌ Échec du refresh au démarrage', error);
				setAuthToken(null);
			});
		} else {
			// Token déjà expiré
			setAuthToken(null);
		}
	}
}

/* ----------------------------
   Interceptor : ajouter token
----------------------------- */
api.interceptors.request.use((config) => {
	const token = localStorage.getItem('token');
	if (token) {
		config.headers.Authorization = `Bearer ${token}`;
	}
	return config;
});

/* ----------------------------
   Interceptor : 401 → refresh token automatique
----------------------------- */
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
	failedQueue.forEach(prom => {
		if (error) {
			prom.reject(error);
		} else {
			prom.resolve(token);
		}
	});
	failedQueue = [];
};

api.interceptors.response.use(
	(response) => response,
	async (error) => {
		const originalRequest = error.config;

		// Si la requête qui échoue est /jwt/refresh elle-même, ne pas réessayer
		if (originalRequest.url?.includes('/jwt/refresh')) {
			setAuthToken(null);
			return Promise.reject(error);
		}

		// Si 401 et on n'a pas encore tenté de refresh
		if (error.response?.status === 401 && !originalRequest._retry) {
			if (isRefreshing) {
				// Si un refresh est déjà en cours, mettre la requête en queue
				return new Promise((resolve, reject) => {
					failedQueue.push({resolve, reject});
				})
					.then(token => {
						originalRequest.headers['Authorization'] = 'Bearer ' + token;
						return api(originalRequest);
					})
					.catch(err => Promise.reject(err));
			}

			originalRequest._retry = true;
			isRefreshing = true;

			const token = localStorage.getItem('token');

			if (!token) {
				// Pas de token, déconnecter
				setAuthToken(null);
				return Promise.reject(error);
			}

			try {
				// Appeler le endpoint refresh
				const response = await axios.post(API_URL + '/jwt/refresh', {}, {
					headers: {
						'Authorization': `Bearer ${token}`
					}
				});

				const newToken = response.data.access_token;
				const newExpiresIn = response.data.expires_in;
				setAuthToken(newToken, newExpiresIn);

				// Traiter la queue des requêtes en attente
				processQueue(null, newToken);

				// Réessayer la requête originale avec le nouveau token
				originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
				return api(originalRequest);
			} catch (refreshError) {
				// Le refresh a échoué, déconnecter l'utilisateur
				processQueue(refreshError, null);
				setAuthToken(null);
				return Promise.reject(refreshError);
			} finally {
				isRefreshing = false;
			}
		}

		return Promise.reject(error);
	}
);